import { Button, Checkbox, Modal, SegmentedControl, Stack } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_PRINT_OPTIONS,
  PrintFormat,
  PrintOptions,
} from 'src/Components/PrintTemplates/PrintablePlanning/PrintablePlanning.types';
import { trackMixpanelEvent } from 'src/Services/Mixpanel';

import styles from './PrintOptionsModal.module.scss';
import { Props } from './PrintOptionsModal.types';

const STORAGE_KEY = 'sh:print-options:v1';

const readStoredOptions = (): PrintOptions => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PRINT_OPTIONS;
    const parsed = JSON.parse(raw) as Partial<PrintOptions>;
    return { ...DEFAULT_PRINT_OPTIONS, ...parsed };
  } catch {
    return DEFAULT_PRINT_OPTIONS;
  }
};

const persistOptions = (options: PrintOptions) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
  } catch {
    // localStorage may be unavailable; degrade silently.
  }
};

const PrintOptionsModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  defaultFormat = 'grid',
}: Props) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<PrintOptions>(readStoredOptions);
  const [format, setFormat] = useState<PrintFormat>(defaultFormat);

  useEffect(() => {
    if (open) {
      setOptions(readStoredOptions());
      setFormat(defaultFormat);
    }
  }, [open, defaultFormat]);

  const toggle = (key: keyof PrintOptions) => () =>
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));

  const handleConfirm = useCallback(() => {
    persistOptions(options);
    trackMixpanelEvent('planning_print_options_confirmed', {
      format,
      showReminders: options.showReminders,
      showSchoolHolidays: options.showSchoolHolidays,
      showPublicHolidays: options.showPublicHolidays,
      showHouseholdLegend: options.showHouseholdLegend,
    });
    onConfirm({ options, format });
  }, [format, onConfirm, options]);

  return (
    <Modal opened={open} onClose={onClose} title={t('planning.print.title')} centered size="md">
      <Stack gap="sm" className={styles.container}>
        <SegmentedControl
          value={format}
          onChange={value => setFormat(value as PrintFormat)}
          data={[
            { value: 'grid', label: t('planning.print.formats.grid', 'Grille du mois') },
            { value: 'stays', label: t('planning.print.formats.stays', 'Frise des séjours') },
          ]}
          fullWidth
        />
        <Checkbox
          checked={options.showReminders}
          onChange={toggle('showReminders')}
          label={t('planning.print.options.reminders')}
          disabled={format === 'stays'}
        />
        <Checkbox
          checked={options.showSchoolHolidays}
          onChange={toggle('showSchoolHolidays')}
          label={t('planning.print.options.schoolHolidays')}
          disabled={format === 'stays'}
        />
        <Checkbox
          checked={options.showPublicHolidays}
          onChange={toggle('showPublicHolidays')}
          label={t('planning.print.options.publicHolidays')}
          disabled={format === 'stays'}
        />
        <Checkbox
          checked={options.showHouseholdLegend}
          onChange={toggle('showHouseholdLegend')}
          label={t('planning.print.options.householdLegend')}
        />
        <div className={styles.actions}>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleConfirm} loading={loading}>
            {t('planning.print.confirm')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
};

export default PrintOptionsModal;
